import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { User } from "react-feather";
import { FORMS, MODULES } from "../../const";
import { gsDivisionValidation } from "../validations/GSDivisionCreation";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import SearchDropdown from "../input/SearchDropdown";
import { countryStartsWith } from "../../api/countryApi";
import InfoList from "../InfoList";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";

const FORM = FORMS.GS_DIVISION.GS_DETAIL.form;

const GSDivisionAddComponent = forwardRef(
  ({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {
    const userCompany = useUserCompany();
    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : 1
    );

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : 1);
    }, [userCompany]);

    const [gsDivisionData, setGsDivisionData] = useState(
      editMode
        ? {
            rgsCode: currentValues?.rgsCode || "",
            rgsComId: currentValues?.rgsComId || company,
            rgsName: currentValues?.rgsName || "",
            rgsCountry: currentValues?.rgsCountry || null,
            rgsDescription: currentValues?.rgsDescription || "",
            rgsStatus: currentValues?.rgsStatus || null,
            rgsActive: currentValues?.rgsActive || null,
            createdBy: currentValues?.createdBy || null,
            createdDate: currentValues?.createdDate || null,
            lastModifiedBy: currentValues?.lastModifiedBy || null,
            lastModifiedDate: currentValues?.lastModifiedDate || null,
          }
        : {
            rgsCode: "",
            rgsComId: company,
            rgsName: "",
            rgsCountry: null,
            rgsDescription: "",
            rgsStatus: 2,
            rgsActive: 1,
          }
    );

    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [currentErrors, setCurrentErrors] = useState({});

    useEffect(() => {
      setCurrentErrors(errors || {});
    }, [errors]);

    // Sync currentValues into form state whenever editMode activates with new data.
    // useState initializer only runs on first mount; the component stays mounted
    // inside SideDrawer, so we must manually sync on every edit open.
    useEffect(() => {
      if (editMode && currentValues) {
        setGsDivisionData({
          rgsCode: currentValues.rgsCode || "",
          rgsComId: currentValues.rgsComId || company,
          rgsName: currentValues.rgsName || "",
          rgsCountry: currentValues.rgsCountry || null,
          rgsDescription: currentValues.rgsDescription || "",
          rgsStatus: currentValues.rgsStatus || null,
          rgsActive: currentValues.rgsActive || null,
          createdBy: currentValues.createdBy || null,
          createdDate: currentValues.createdDate || null,
          lastModifiedBy: currentValues.lastModifiedBy || null,
          lastModifiedDate: currentValues.lastModifiedDate || null,
        });
        setSearchFieldReset(true);
      }
    }, [editMode, currentValues]);

    useImperativeHandle(ref, () => ({
      getData: () => ({
        rgsComId: company,
        rgsName: gsDivisionData.rgsName,
        rgsCountry: gsDivisionData.rgsCountry,
        rgsDescription: gsDivisionData.rgsDescription,
        rgsStatus: gsDivisionData.rgsStatus,
        rgsActive: gsDivisionData.rgsActive,
        createdBy: gsDivisionData.createdBy,
        createdDate: gsDivisionData.createdDate,
        lastModifiedBy: gsDivisionData.lastModifiedBy,
        lastModifiedDate: gsDivisionData.lastModifiedDate,
      }),
      validateForm: () => {
        const validationErrors = gsDivisionValidation(gsDivisionData);
        const combinedErrors = { ...currentErrors, ...validationErrors };
        setCurrentErrors(combinedErrors);
        return combinedErrors;
      },
      setDuplicateError: (msg) => {
        setCurrentErrors((prev) => ({
          ...prev,
          rgsName: msg || "A record with this name already exists.",
        }));
      },
      resetForm: () => {
        setGsDivisionData({
          rgsCode: "",
          rgsComId: company,
          rgsName: "",
          rgsCountry: null,
          rgsDescription: "",
          rgsStatus: 2,
          rgsActive: 1,
        });
        setCurrentErrors({});
        setSearchFieldReset(true);
      },
    }));

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Grama Niladhari Division Details</h4>
              <div className="two fields">
                <Input
                  label="Grama Niladhari Division Code"
                  placeholder="Auto-generated"
                  module={MODULES.Master}
                  form={FORM}
                  field="rgs_code"
                  editMode={editMode}
                  disabled={true}
                  name="rgsCode"
                  value={gsDivisionData.rgsCode}
                  onChange={() => {}}
                />
                <Input
                  required={true}
                  label="Grama Niladhari Division Name"
                  placeholder="Grama Niladhari Division Name"
                  module={MODULES.Master}
                  form={FORM}
                  field="rgs_name"
                  editMode={editMode}
                  error={currentErrors.rgsName}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rgsName: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  blockPattern={/[^a-zA-Z\s.'-]/g}
                  duplicate={false}
                  activeCol="rgs_active"
                  companyCol="rgs_com_id"
                  companyWise={true}
                  tableName="masterdata.tms_m_reg_gs"
                  primaryKey="rgs_id"
                  searchField="rgs_name"
                  keyType="S"
                  duplicateErrorMessage="A record with this name already exists."
                  setErrors={setCurrentErrors}
                  errorField="rgsName"
                  id={editMode ? currentValues?.rgsId : null}
                  name="rgsName"
                  value={gsDivisionData.rgsName}
                  onChange={(e) => {
                    setGsDivisionData((prevState) => ({
                      ...prevState,
                      rgsName: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <Input
                  label="Company"
                  module={MODULES.Master}
                  form={FORM}
                  field="rgs_com_id"
                  editMode={editMode}
                  disabled={true}
                  readOnly={true}
                  name="rgsComId"
                  value={
                    userCompany?.comCompany ||
                    userCompany?.comName ||
                    (company ? `Company ${company}` : "")
                  }
                />
                <SearchDropdown
                  key={1}
                  editMode={editMode}
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setGsDivisionData((prevState) => ({
                      ...prevState,
                      rgsCountry: null,
                    }));
                  }}
                  onResultSelect={(data) => {
                    setGsDivisionData((prevState) => ({
                      ...prevState,
                      rgsCountry: data,
                    }));
                  }}
                  api={countryStartsWith}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_reg_country",
                    primaryKey: "cou_id",
                    fieldName: "cou_country",
                    id: gsDivisionData.rgsCountry,
                  }}
                  defaultValue={currentValues?.couCountry || ""}
                  label="Country"
                />
              </div>
              <div className="two fields">
                <div className="field">
                  <Input
                    name="rgsDescription"
                    label="Description"
                    module={MODULES.Master}
                    form={FORM}
                    field="rgs_description"
                    editMode={editMode}
                    placeholder="Description"
                    value={gsDivisionData.rgsDescription}
                    onChange={(e) => {
                      setGsDivisionData((prevState) => ({
                        ...prevState,
                        rgsDescription: e.target.value,
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Status"
                  error={currentErrors.rgsStatus}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="rgs_status"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rgsStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.statusOptions || []}
                  style={{ height: "20px" }}
                  placeholder="Status"
                  value={gsDivisionData.rgsStatus}
                  onChange={(e, data) =>
                    setGsDivisionData((prev) => ({
                      ...prev,
                      rgsStatus: data.value,
                    }))
                  }
                />
                <Dropdown
                  required={true}
                  label="Active"
                  error={currentErrors.rgsActive}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="rgs_active"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rgsActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.activeOptions || []}
                  style={{ height: "20px" }}
                  placeholder="Active"
                  value={gsDivisionData.rgsActive}
                  onChange={(e, data) =>
                    setGsDivisionData((prev) => ({
                      ...prev,
                      rgsActive: data.value,
                    }))
                  }
                />
              </div>
            </div>
            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: gsDivisionData.createdBy
                      ? gsDivisionData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: gsDivisionData.createdDate
                      ? formatFromJsonDate(gsDivisionData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: gsDivisionData.lastModifiedBy
                      ? gsDivisionData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: gsDivisionData.lastModifiedDate
                      ? formatFromJsonDate(gsDivisionData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  }
);

export default GSDivisionAddComponent;
